/**
 * DimensionManager - Manages switching between Overworld, Nether, and The End.
 *
 * Coordinate mapping:
 *   Nether coordinates = Overworld coordinates / 8
 *   End coordinates = 1:1 (fixed spawn at origin)
 */

export class DimensionManager {
  constructor() {
    /** @type {'overworld' | 'nether' | 'end'} */
    this.currentDimension = 'overworld';

    /** Last overworld position (for returning from Nether/End) */
    this._overworldReturnPos = null;

    /** Last nether position (for returning from Overworld portal) */
    this._netherReturnPos = null;

    /** Last end position */
    this._endReturnPos = null;

    /** Cooldown timer to prevent rapid dimension switching */
    this._portalCooldown = 0;

    /** Seconds before portal can be used again */
    this.PORTAL_COOLDOWN_TIME = 4.0;

    /** Whether the dragon has been defeated */
    this.dragonDefeated = false;
  }

  /**
   * Switch from Overworld to Nether.
   */
  switchToNether(playerPos) {
    this._overworldReturnPos = { x: playerPos.x, y: playerPos.y, z: playerPos.z };
    this.currentDimension = 'nether';
    this._portalCooldown = this.PORTAL_COOLDOWN_TIME;

    if (this._netherReturnPos) {
      return { ...this._netherReturnPos };
    }

    return {
      x: Math.floor(playerPos.x / 8),
      y: playerPos.y,
      z: Math.floor(playerPos.z / 8),
    };
  }

  /**
   * Switch from Nether to Overworld.
   */
  switchToOverworld(playerPos) {
    this._netherReturnPos = { x: playerPos.x, y: playerPos.y, z: playerPos.z };
    this.currentDimension = 'overworld';
    this._portalCooldown = this.PORTAL_COOLDOWN_TIME;

    if (this._overworldReturnPos) {
      return { ...this._overworldReturnPos };
    }

    return {
      x: Math.floor(playerPos.x * 8),
      y: playerPos.y,
      z: Math.floor(playerPos.z * 8),
    };
  }

  /**
   * Switch from Overworld to The End.
   */
  switchToEnd(playerPos) {
    this._overworldReturnPos = { x: playerPos.x, y: playerPos.y, z: playerPos.z };
    this.currentDimension = 'end';
    this._portalCooldown = this.PORTAL_COOLDOWN_TIME;

    // End always spawns at the fixed platform on the main island
    return { x: 0, y: 52, z: 0 };
  }

  /**
   * Switch from The End to Overworld (after dragon defeat or exit portal).
   */
  switchFromEnd(playerPos) {
    this._endReturnPos = { x: playerPos.x, y: playerPos.y, z: playerPos.z };
    this.currentDimension = 'overworld';
    this._portalCooldown = this.PORTAL_COOLDOWN_TIME;

    if (this._overworldReturnPos) {
      return { ...this._overworldReturnPos };
    }

    return { x: 0, y: 64, z: 0 };
  }

  isInNether() {
    return this.currentDimension === 'nether';
  }

  isInEnd() {
    return this.currentDimension === 'end';
  }

  getOverworldReturnPos() {
    return this._overworldReturnPos;
  }

  getNetherReturnPos() {
    return this._netherReturnPos;
  }

  update(dt) {
    if (this._portalCooldown > 0) {
      this._portalCooldown -= dt;
    }
  }

  canUsePortal() {
    return this._portalCooldown <= 0;
  }

  getDimensionName() {
    switch (this.currentDimension) {
      case 'nether': return 'Nether';
      case 'end': return 'El End';
      default: return 'Mundo Principal';
    }
  }
}
